import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import {
  Destroyable,
  ChannelNameTokens,
  Rental,
  selectRentalById,
  untilDestroy,
  Channel,
  isSomething,
  RatesChangedComplete,
  GroupRatesChangedComplete,
  ActionFailed,
} from '@tokeet-frontend/tv3-platform'
import { merge, Observable, of } from 'rxjs'
import { catchError, debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators'
import { PushRatesToConnection, PushRatesToConnectionComplete } from '@tv3/store/connection/connection.actions'
import { Connection, isRecentlyLinked, stampedSinceLink } from '@tv3/store/connection/connection.model'
import { ConnectionHelperService } from '../../../connection-helper.service'
import { EditRateDialogService } from '@tv3/containers/rates/edit-rate-dialog/edit-rate-dialog.service'
import { selectConnectionById } from '@tv3/store/connection/connection.selectors'
import { AirbnbHelperService } from '@tv3/containers/channels/channel-connect/airbnb-helper.service'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { GetChannelRoomRatesPayload } from '@tv3/store/connection/connection.types'
import * as lodash from 'lodash'

// Lifecycle of the hands-off rate push that runs when this step becomes active.
// 'missing' and 'manual' are the two states that genuinely need the user.
type AutoPushRatesState = 'pushing' | 'done' | 'failed' | 'missing' | 'manual' | null

@Component({
  selector: 'app-airbnb-listing-wizard-step3',
  templateUrl: './airbnb-listing-wizard-step3.component.html',
  styleUrls: ['./airbnb-listing-wizard-step3.component.scss'],
})
export class AirbnbListingWizardStep3Component extends Destroyable implements OnInit, OnChanges {
  @Input() connection: Connection
  @Input() channel: Channel
  // True while this step is the one selected in the stepper. The auto push must not
  // fire from ngOnInit: the stepper instantiates every step up front, and rates would
  // otherwise be pushed while the user is still linking on the first step.
  @Input() isActive = false
  rental: Rental

  @Output() prev = new EventEmitter()
  @Output() next = new EventEmitter()

  isRatesPushed = false
  isRentalRatesReady = true
  ratesMissing: string[] = []

  autoPushState: AutoPushRatesState = null
  autoPushedRateName = ''
  private autoPushAttempted = false

  constructor(
    private actions: Actions,
    private store: Store<any>,
    private connectionHelperService: ConnectionHelperService,
    private editRateDialog: EditRateDialogService,
    private airbnbHelperService: AirbnbHelperService,
    private connectionService: ConnectionService
  ) {
    super()
  }

  ngOnInit(): void {
    // A rate push recorded on the connection survives closing the wizard. Only a push
    // for the CURRENT link counts (TV4-5891): the stamp is keyed to the channel room and
    // survives an unlink/re-link, and this flag only ever goes up — an unlinked room
    // (no linkDate yet) must not seed it either, or the re-link would skip the push.
    this.isRatesPushed = this.isRatesPushed || this.isRatesPushedOnConnection(this.connection)

    const rental$ = this.store.pipe(
      select(selectConnectionById, { id: this.connection.id }),
      tap((c) => {
        this.connection = c
        this.isRatesPushed = this.isRatesPushed || this.isRatesPushedOnConnection(c)
      }),
      switchMap((c) => this.store.pipe(select(selectRentalById(c.rentalId)))),
      tap((rental) => (this.rental = rental))
    )

    // Review-and-confirm: show what rate information is still missing, refreshed
    // whenever the rental or its rates change.
    merge(rental$, this.actions.pipe(ofType(RatesChangedComplete, GroupRatesChangedComplete)))
      .pipe(
        debounceTime(500),
        map(() => this.rental),
        filter(isSomething),
        switchMap((rental: Rental) => this.connectionHelperService.getRentalRatesReadiness(rental, this.connection)),
        untilDestroy(this)
      )
      .subscribe(({ ready, missing }) => {
        this.isRentalRatesReady = ready
        this.ratesMissing = missing
        // The user supplied what was missing: give the hands-off push another go.
        if (ready && this.autoPushState === 'missing') {
          this.autoPushAttempted = false
          this.maybeAutoPushRates()
        }
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isActive && this.isActive) {
      this.maybeAutoPushRates()
    }
  }

  // Pushes rates without a dialog when there is nothing to ask: the rental's rate data is
  // complete and Airbnb's default (Standard) rate plan can be resolved — the exact values
  // the Select Rate Category dialog would preselect. Anything short of that hands over to
  // the manual Push Rates flow below.
  private isRatesPushedOnConnection(connection: Connection): boolean {
    return !!connection?.rentalId && stampedSinceLink(connection, connection?.lastratepush)
  }

  private maybeAutoPushRates() {
    if (!this.isActive || this.autoPushAttempted || this.isRatesPushed || !isRecentlyLinked(this.connection)) {
      return
    }
    this.autoPushAttempted = true

    this.store
      .pipe(
        select(selectConnectionById, { id: this.connection.id }),
        take(1),
        switchMap((connection) =>
          this.store.pipe(
            select(selectRentalById(connection.rentalId)),
            take(1),
            map((rental) => ({ connection, rental }))
          )
        ),
        switchMap(({ connection, rental }) => {
          if (!rental) {
            this.autoPushState = 'manual'
            return of(false)
          }
          return this.connectionHelperService.getRentalRatesReadiness(rental, connection).pipe(
            switchMap(({ ready }) => {
              if (!ready) {
                // The banner above the accordion lists what is missing.
                this.autoPushState = 'missing'
                return of(false)
              }
              return this.doAutoPushRates(connection)
            })
          )
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private doAutoPushRates(connection: Connection): Observable<boolean> {
    this.autoPushState = 'pushing'

    const payload: GetChannelRoomRatesPayload = {
      api: ChannelNameTokens.AirBnBV2API,
      channel_id: connection.channelId,
      id: connection.roomId,
      propertyId: connection.propertyId,
    }

    return this.connectionService
      .getRoomRatesFromConnection(ChannelNameTokens.AirBnBV2API, connection.channelId, payload)
      .pipe(
        // Same default the Select Rate Category dialog preselects.
        map(
          (rates) => lodash.find(rates, (rate) => lodash.toLower(rate.name).includes('standard')) || lodash.first(rates)
        ),
        switchMap((channelRate) => {
          if (!channelRate) {
            this.autoPushState = 'manual'
            return of(false)
          }
          this.autoPushedRateName = channelRate.name
          this.store.dispatch(
            PushRatesToConnection({
              connId: connection.id,
              channelId: connection.channelId,
              channelName: this.channel.name,
              data: {
                category: 'default',
                rateid: channelRate.id,
                maxguest: channelRate.maxguest,
                pricemodel: channelRate.model,
                roomId: connection.roomId,
                propertyId: connection.propertyId,
                rental_id: connection.rentalId,
                maxPersons: channelRate.max_per,
                name: channelRate.name,
              },
            })
          )
          return this.actions.pipe(
            ofType(PushRatesToConnectionComplete, ActionFailed),
            take(1),
            map((action) => {
              const ok = action.type === PushRatesToConnectionComplete.type
              this.isRatesPushed = this.isRatesPushed || ok
              this.autoPushState = ok ? 'done' : 'failed'
              return ok
            })
          )
        }),
        catchError(() => {
          this.autoPushState = 'manual'
          return of(false)
        })
      )
  }

  onPriceSettings() {
    return this.airbnbHelperService.updatePriceSettingsV2(this.connection)
  }

  onAddStandardRate() {
    this.addRentalRate('standard')
  }

  onAddDynamicRate() {
    this.addRentalRate('dynamic')
  }

  onPushRates() {
    this.connectionHelperService
      .pushRatesForAPIConnection(this.channel, this.rental, this.connection)
      .pipe(
        switchMap(() => this.actions.pipe(ofType(PushRatesToConnectionComplete))),
        tap(() => {
          this.isRatesPushed = true
          // A manual push supersedes whatever the hands-off attempt reported.
          this.autoPushState = null
        }),
        take(1),
        untilDestroy(this)
      )
      .subscribe()
  }

  addRentalRate(type: 'standard' | 'dynamic') {
    this.editRateDialog.open({
      rentals: [this.rental],
      rental: this.rental,
      type,
    })
  }
}
